import { getAnnouncementsV4 } from 'api/canguard/getAnnouncementsV4';
import React, { useEffect, useState } from 'react';
import styles from './NotificationsTab.module.css';
import { Box, Card, CardContent, CircularProgress, Divider, Link, Paper, Typography } from '@mui/material';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';

interface Announcement {
	ItemID: number;
	Title: string;
	Description: string;
	MoreLink: string | null;
	CreatedDate: string | null;
	ExpireDate: string | null;
}

const NotificationsTab: React.FC = () => {
	const [announcements, setAnnouncements] = useState<Announcement[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const { notifyError } = useNotificationProvider();

	const fetchAnnouncements = async () => {
		setLoading(true);
		try {
			const data = await getAnnouncementsV4();

			if (Array.isArray(data)) {
				const filtered = data.filter(
					(item) =>
						(item.Title && item.Title.trim() !== '') ||
						(item.Description && item.Description.trim() !== ''),
				);
				setAnnouncements(filtered);
			} else {
				console.error(
					'[NotificationsTab/fetchAnnouncements] Announcements data is not an array',
				);
			}
		} catch (error) {
			console.error(
				'[NotificationsTab/fetchAnnouncements] Failed to fetch announcements:',
				error,
			);
			notifyError(
				'Could not load notifications. Please try again later.',
			);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchAnnouncements();
	}, []);

	return (
		<div className={styles.wrapper}>
			<Typography
				variant="h5"
				sx={{ fontWeight: '300' }}
				color={'primary'}
			>
				Announcements
			</Typography>
			<Paper elevation={1} sx={{ p: 2, overflow: 'auto', flex: 1 }}>
			<div className={styles.content}>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress size={40} />
					</Box>
				) : announcements.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
						No notifications at this time.
					</Typography>
				) : (
					<>
						{announcements.map((announcement) => (
							<Box key={announcement.ItemID}>
								<Card>
									<CardContent>
										<Typography
											variant="body2"
											className={styles.title}
										>
											{announcement.Title}
										</Typography>
										<Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
											{announcement.Description}
										</Typography>
										{announcement.MoreLink &&
											announcement.MoreLink.trim() !== '' && (
											<>
												<Divider sx={{ mt: 2 }} />
												<Link
													href={announcement.MoreLink}
													target="_blank"
													rel="noopener noreferrer"
													variant="caption"
													sx={{
														display: 'flex',
														alignItems: 'center',
														mt: 1,
														fontWeight: 600,
													}}
												>
													Read More ›
												</Link>
											</>
										)}
									</CardContent>
								</Card>
							</Box>
						))}
					</>
				)}
			</div>
			</Paper>
		</div>
	);
};

export default NotificationsTab;
